import { RefObject, useCallback, useEffect, useRef } from 'react';

type Props<T extends HTMLElement> = {
  containerRef: RefObject<T | null>;
  onClickOutside: (event: TouchEvent | MouseEvent) => void;
};

export function useOnClickOutside<T extends HTMLElement>({
  containerRef,
  onClickOutside,
}: Props<T>) {
  const isTouchRef = useRef(false);

  const onClickOutsideRef = useRef(onClickOutside);
  onClickOutsideRef.current = onClickOutside;

  const handle = useCallback(
    (event: TouchEvent | MouseEvent) => {
      const container = containerRef.current;

      if (
        container &&
        event.target instanceof Node &&
        !container.contains(event.target)
      ) {
        onClickOutsideRef.current(event);
      }
    },
    [containerRef]
  );

  const handleTouch = useCallback(
    (event: TouchEvent) => {
      isTouchRef.current = true;
      handle(event);
    },
    [handle]
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (isTouchRef.current) return;
      handle(event);
    },
    [handle]
  );

  useEffect(() => {
    document.addEventListener('touchend', handleTouch, true);
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('touchend', handleTouch, true);
      document.removeEventListener('click', handleClick, true);
    };
  }, [handleClick, handleTouch]);
}
